import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function CostTypeDistributionPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.costTypeDistribution();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Cost Type Distribution</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Distinct cost_type values in property_recurring_costs with counts
          and which properties use them. Compares data values against the
          PropertyCostType enum.
        </p>

        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run
        </Button>

        {result && (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span>Total cost records: <strong>{result.total_cost_records}</strong></span>
              <span>Distinct types: <strong>{result.distinct_cost_types}</strong></span>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">cost_type</th>
                    <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    <th className="text-right px-2 py-1.5 font-medium">Properties</th>
                    <th className="text-left px-2 py-1.5 font-medium">Platforms</th>
                    <th className="text-left px-2 py-1.5 font-medium">Currencies</th>
                  </tr>
                </thead>
                <tbody>
                  {result.buckets?.map((b) => (
                    <tr key={b.cost_type} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono font-medium">{b.cost_type}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{b.count}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{b.property_count}</td>
                      <td className="px-2 py-1">
                        {b.platforms?.map((p) => (
                          <Badge key={p} variant="outline" className="text-[10px] mr-1">{p}</Badge>
                        ))}
                      </td>
                      <td className="px-2 py-1">
                        {b.currencies?.map((c) => (
                          <Badge key={c} variant="outline" className="text-[10px] mr-1">{c}</Badge>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {result.buckets?.map((b) => (
              b.properties?.length > 0 && (
                <details key={`props-${b.cost_type}`} className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Properties using <span className="font-mono font-medium">{b.cost_type}</span> ({b.property_count})
                  </summary>
                  <div className="mt-1 flex flex-wrap gap-1 pl-4">
                    {b.properties.map((p) => (
                      <Badge key={p.property_id} variant="outline" className="text-[10px] font-mono">
                        {p.name} ({p.property_id})
                      </Badge>
                    ))}
                  </div>
                </details>
              )
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
